import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { Area } from './area.interface';
import { ClusterService } from '../cluster/cluster.service';
import { first, filter } from 'rxjs/operators';
import { ApiService } from '../../api/api.service';
import { ProjectService } from '../project/project.service';
import { BuildingService } from '../building/building.service';
import { BasicArea } from '../basic-area/basic-area.interface';
import { Cluster } from '../cluster/cluster.interface';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Area core service.
 *
 * Contains all the private logic for Area management.
 *
 * **Only AreaService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class AreaCoreService {

  /** Areas subject. */
  protected _area: Subject<Area[]> = new BehaviorSubject<Area[]>(null);

  /** Areas observable. */
  public area$ = this._area.asObservable();

  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected buildingService: BuildingService,
    protected clusterService: ClusterService,
    protected projectService: ProjectService
  ) {
    this._trackClusters();
  }

  /**
   * Get areas.
   *
   * @returns The current areas.
   */
  public get(): Area[] {
    let areas: Area[];
    this.area$.pipe(first()).subscribe(a => areas = a.filter(ar => ar.active));
    return areas;
  }

  /**
   * Get specific area.
   *
   * @note There seems to be a bug somehwere (Elements & filter page?) where the area's clusterId
   *  is removed, which makes the area-detail page crash when visiting it directly after the
   *  Elements & filter page:
   *    - Area detail page > Elements & filters > back button.
   *
   * @param id The id of the area.
   * @returns The requested area.
   */
  public getArea(id: Area['_id']): Area {
    const area = this.get().find(a => a._id === id);
    return {
      ...area,
      clusterId: area && this.clusterService.getClusterByAreaId(area._id)._id || null
    };
  }

  /**
   * Get the parent area for a basic area.
   *
   * @param id The id of the basic area.
   * @returns The area.
   */
  public getAreasByBasicAreaId(id: BasicArea['_id']): Area[] {
    return this.get().filter(a => a.active && a.basicAreaId === id);
  }

  /**
   * Get the area's properties.
   *
   * Greatly reduces size of the returned area.
   *
   * @param id The id of the area.
   * @returns The requested area.
   */
  public getAreaProperties(id: Area['_id']): Partial<Area> {
    const area = JSON.parse(JSON.stringify(this.getArea(id))) as Area;
    for (const prop of Object.keys(area)) {
      if (typeof area[prop] === 'object' || Array.isArray(area[prop])) {
        delete area[prop];
      }
    }
    return area;
  }

  /**
   * Add new area.
   *
   * @param template The template of the area to add.
   * @return Observable with new area.
   */
  protected _add(template: Partial<Area>): Observable<Area> {
    const area = {
      ...template,
      active: true
    } as Area;
    return new Observable(observer => {
      this.apiService.addArea(template).subscribe(response => {
        response.buildingId = area.buildingId;
        response.clusterId = area.clusterId;
        this.projectService.addArea(response);
        observer.next(response);
        observer.complete();
      });
    });
  }

  /**
   * Fixes Area models.
   *
   * Adds properties on init, to prevent performance heavy lookups later on.
   *
   * - `basicAreaId`: When the area is a modified basic area.
   * - `buildingId`: Parent building id.
   * - `clusterId`: Parent cluster id.
   *
   * @todo Remove 'basicArea' property from an area (set on legacy area detail page).
   * @todo Remove `basicAreas` property from an area (set on legacy area overview page).
   */
  private _fix(areas: Area[], cluster: Cluster): Area[] {
    for (const area of areas) {
      if (area.basicArea) {
        area.basicAreaId = typeof area.basicArea === 'string'
          ? String(area.basicArea)
          : area.basicArea._id;
      }
      area.buildingId = cluster.buildingId;
      area.clusterId = cluster._id;

      // delete area.basicArea;
      // delete area.basicAreas;
      // delete area.getAreaTotalFNO;
    }
    return areas;
  }

  /**
   * Patch area.
   *
   * @todo Remove method and move logic to ProjectService.
   *
   * @param area The area to patch.
   * @param values The updated values.
   * @param [config] Optional configuration.
   * @returns Observable with state.
   */
  protected _patchArea(area: Area, values: Partial<Area>, options?: { local: boolean }): Observable<boolean> {
    return new Observable(observer => {
      if (!options?.local) {
        this.apiService.updateArea(area, values).subscribe(response => {
          this.projectService.patchArea(area, values);
          observer.next(true);
          observer.complete();
        });
      } else {
        this.projectService.patchArea(area, values);
        observer.next(true);
        observer.complete();
      }
    });
  }

  /**
   * Track project and set clusters.
   */
  protected _trackClusters() {
    this.clusterService.cluster$.pipe(
      filter(clusters => clusters !== null)
    ).subscribe(clusters => {
      const areas = [];
      clusters.forEach(cluster => {
        areas.push(...this._fix(cluster.areas, cluster));
      });
      this._area.next(areas);
    });
  }
}
